import React from "react";
import {
  Grid,
  makeStyles,
  Typography,
  TextField,
  Paper,
  Button,
  IconButton,
} from "@material-ui/core";
import { ValidatorForm, TextValidator } from 'react-material-ui-form-validator';
import useError from "Hooks/useError";
import MuiAlert from "@material-ui/lab/Alert";
import { message } from "constants/message";
import {
  MuiPickersUtilsProvider,
  DateTimePicker,
} from "@material-ui/pickers";
import ClearIcon from "@material-ui/icons/Clear";
import DateFnsUtils from "@date-io/date-fns";

import "dayjs/locale/ja";
import dayjs from "dayjs";
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
dayjs.locale("ja");
dayjs.extend(isSameOrAfter);
dayjs.extend(isSameOrBefore);


const useStyles = makeStyles((theme) => ({
  paper: {
    padding: theme.spacing(2),
    display: "flex",
    overflow: "auto",
    flexDirection: "column",
  },
  wrapper: {
    marginTop: theme.spacing(2),
  },
  dateWrapper: {
    display: "flex",
    marginTop: theme.spacing(2),
  },
  divider: {
    display: "flex",
    alignItems: "center",
    margin: theme.spacing(2),
  },
  clearButton: {
    width: "32px",
    height: "32px",
  },
  contentWrapper: {
    border: "1px solid",
    padding: theme.spacing(1),
    width: "600px",
  },
  contentTextField: {
    width: "568px",
  },
  messsageWrapper: {
    marginTop: theme.spacing(1),
  },
}));

export default function Form({ submit, dtm = null, isEdit = false }) {
  const classes = useStyles();
  const stateError = useError();
  const messages = message();

  const [validFrom, setValidFrom] = React.useState(null);
  const [validUntil, setValidUntil] = React.useState(null);
  const [label, setLabel] = React.useState("");
  const [desc, setDesc] = React.useState("");
  const [dtmMessage, setDtmMessage] = React.useState("");
  const [textFieldHeight, setTextFieldHeight] = React.useState(1);

  React.useEffect(() => {
    stateError.initialize();
  }, []);

  React.useEffect(() => {
    if (!dtm) return;
    setValidFrom(dtm.validFrom);
    setValidUntil(dtm.validUntil);
    setLabel(dtm.label);
    setDesc(dtm.desc);
    setDtmMessage(dtm.message);
  }, [dtm]);

  React.useEffect(() => {
    // 告知内容の改行数に合わせてテキストエリアの高さを変更
    const rowMessage = dtmMessage.split("\n");
    setTextFieldHeight(rowMessage.length);
  }, [dtmMessage]);

  const dateValidation = (date, type) => {
    // 入力内容が日付として正しいかチェック
    if (dayjs(date, "YYYY/MM/DD HH:mm:ss").isValid()) {
      if (type == "validFrom") {
        const dateTime = dayjs(validUntil).format("YYYY-MM-DD HH:mm:ss")
        const selectedDateTime = dayjs(date).format("YYYY-MM-DD HH:mm:ss")
        // 比較対象がnullなため比較なしでset
        // 終了日付が開始日付より後の日かチェック
        if (!dayjs(dateTime).isValid() || (dayjs(dateTime).isSameOrAfter(dayjs(selectedDateTime)))) {
          setValidFrom(dayjs(date).format("YYYY-MM-DD HH:mm:ss"));
        } else {
          alert(messages.validation.dateRang);
        }
      } else if (type == "validUntil"){
        const dateTime = dayjs(validFrom).format("YYYY-MM-DD HH:mm:ss")
        const selectedDateTime = dayjs(date).format("YYYY-MM-DD HH:mm:ss")
        // 比較対象がnullなため比較なしでset
        // 開始日付が終了日付より前の日かチェック
        if (!dayjs(dateTime).isValid() || (dayjs(dateTime).isSameOrBefore(dayjs(selectedDateTime)))) {
          setValidUntil(dayjs(date).format("YYYY-MM-DD HH:mm:ss"));
        } else {
          alert(messages.validation.dateRang);
        }
      }
      return;
    }
    return;
  }

  function clearValidFrom(e) {
    e.stopPropagation();
    setValidFrom(null);
  }

  function clearValidUntil(e) {
    e.stopPropagation();
    setValidUntil(null);
  }

  const onError = () => {
    stateError.dispatch({
      type: "set",
      payload: {
        isError: true,
        errorMsg: messages.validation.common,
      },
    });
  };

  return (
    <Grid>
      <ValidatorForm
        onSubmit={(e) => {
          e.preventDefault();
          submit(
            { 
              validFrom,
              validUntil,
              label,
              desc,
              dtmMessage,
            }
          );
        }}
        onError={onError}
      >
        <Paper elevation={3} className={classes.paper}>
          <Typography variant="h6" color="primary">
            {isEdit ? "DTM告知更新" : "DTM告知登録"}
          </Typography>
          <Grid container>
            <Grid item xs={12} className={classes.dateWrapper}>
              <MuiPickersUtilsProvider utils={DateFnsUtils}>
                <DateTimePicker
                  label="掲載開始日"
                  format="yyyy/MM/dd HH:mm:ss"
                  value={validFrom}
                  onChange={(date) => dateValidation(date, "validFrom")}
                  InputProps={{
                    endAdornment: (
                      <IconButton 
                        className={classes.clearButton} 
                        onClick={(e) => clearValidFrom(e)}>
                          <ClearIcon />
                      </IconButton>
                    )
                  }}
                />
              </MuiPickersUtilsProvider>
              <div className={classes.divider}>〜</div>
              <MuiPickersUtilsProvider utils={DateFnsUtils}>
                <DateTimePicker
                  label="掲載終了日"
                  format="yyyy/MM/dd HH:mm:ss"
                  value={validUntil}
                  onChange={(date) => dateValidation(date, "validUntil")}
                  InputProps={{
                    endAdornment: (
                      <IconButton 
                        className={classes.clearButton} 
                        onClick={(e) => clearValidUntil(e)}>
                          <ClearIcon />
                      </IconButton>
                    )
                  }}
                />
              </MuiPickersUtilsProvider>
            </Grid>
            <Grid item xs={12} className={classes.wrapper}>
              <TextValidator
                label="告知ラベル*"
                fullWidth
                value={label}
                onChange={(event) => {
                  setLabel(event.target.value);
                }}
                type="text"
                validators={['required']}
                errorMessages={[
                  messages.validation.required, 
                ]}
              />
            </Grid>
            <Grid item xs={12} className={classes.wrapper}>
              <TextValidator
                label="概要*"
                fullWidth
                value={desc}
                onChange={(event) => {
                  setDesc(event.target.value);
                }}
                type="text"
                validators={['required']}
                errorMessages={[
                  messages.validation.required, 
                ]}
              />
            </Grid>
            <Grid item xs={12} className={classes.wrapper}>
              <Typography variant="h6">
                告知内容
              </Typography>
              <div className={classes.contentWrapper}>
                <Typography>
                  ■□■━━━━━━━━━━━━━━━━━━━━━━━━━ yyyy-MM-dd ━ <br />
                  <br />
                  BuzzFinderデイリートピックメール <br />
                  <br />
                  ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━■□■ <br />
                  <br />
                  BuzzFinderデイリートピックメールをご利用いただきありがとうございます。<br />
                  前日分MM月dd日のデイリートピックメールをお送りしますのでご活用ください。<br />
                  また、詳しい情報はBuzzFinderシステムへログインしてご確認下さい。<br />
                  https://buzzfinder.jp/<br />
                  <br />
                  <br />
                  ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━<br />
                  &lt;本文&gt;<br />
                  ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━<br />
                  &lt;告知内容&gt;<br />
                </Typography>
                <TextField
                  label="告知内容*"
                  multiline
                  minRows={textFieldHeight}
                  maxRows={textFieldHeight}
                  value={dtmMessage}
                  onChange={(event) => {
                    setDtmMessage(event.target.value);
                  }}
                  className={classes.contentTextField}
                />
                <Typography>
                  ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━<br />
                  ▼配信設定の変更について<br />
                  登録収集グループの変更、通知先メールアドレスの変更・停止については<br />
                  BuzzFinderにログインいただき、設定の変更をお願いします。<br />
                  URL:https://buzzfinder.jp/<br />
                  <br />
                  ■□■━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━<br />
                  <br />
                  発行：NTTコム オンライン・マーケティング・ソリューション株式会社<br />
                  　　　東京都品川区大崎1-5-1 大崎センタービル4F<br />
                  <br />
                  ※本メールはBuzzFinderシステムより自動配信されております。<br />
                  　 本メールへの返信はできませんのでご了承下さい。<br />
                  <br />
                  ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━■□■<br />
                </Typography>
              </div>
            </Grid>
          </Grid>
        </Paper>
        {stateError.state.isError && (
          <div className={classes.messsageWrapper}>
            <MuiAlert
              elevation={6}
              variant="filled"
              severity="error"
              className={classes.toast}
            >
              {stateError.state.errorMsg}
            </MuiAlert>
          </div>
        )}
        <Grid container justifyContent="center" className={classes.wrapper}>
          <Button type="submit" variant="contained" color="primary">
            {isEdit ? "更新" : "登録"}
          </Button>
        </Grid>
      </ValidatorForm>
    </Grid>
  );
}
